//TODO - https://sim.amazon.com/issues/RCMRX-10803
// This is only placeholder code
import { ScheduleType } from '@flexible-reports/store/api/subscriptions/types';
import { MetricData } from '@flexible-reports/store/slices/metrics/metrics-types';
import { TaktApiMapConfig } from '@flexible-reports/hooks/takt-logger/takt-map-config';
import { SUBSCRIPTION_TABLE_COLUMN_ID } from '@flexible-reports/pages/landing/subscriptions-table/column-configurations/column-id';
import { ReportFilter } from '@flexible-reports/store/slices/online-report-slices/types';

export const TAKT_NAMESPACE_FOR_FRB = 'urc-frb';

export type TaktEventProps =
  | { source: EventSource; eventName: EventName; eventType?: EventType }
  | { source: ApiProvider; eventName: ApiOperation; eventType?: EventType };

export interface TaktLoggerProps {
  event: TaktEventProps;
  payload?:
    | SubscriptionDetails
    | SearchTerm
    | OldAndNewColumnList
    | CustomizeColumnList
    | TemplateId
    | PageSize
    | MetricData
    | Filters
    | FailedReportStatus
    | SubscriptionStateUpdate
    | AccountsPayload
    | FilterValuePayload
    | TemplateModifications
    | ReportPeriodModification;
}

export type ApiMapConfigKeys = keyof typeof TaktApiMapConfig;
export type ApiProvider = (typeof TaktApiMapConfig)[ApiMapConfigKeys]['provider'];
export type ApiOperation = (typeof TaktApiMapConfig)[ApiMapConfigKeys]['operation'];

export const enum EventSource {
  MANAGE_REPORTS_PAGE = 'manage-reports-page',
  SUBSCRIPTION_TABLE = 'subscription-table',
  NEW_REPORT_PAGE = 'new-report-page',
  VIEW_SUBSCRIPTION_PAGE = 'view-subscription-page',
  REPORT_HISTORY = 'report-history',
  REPORT_SETTINGS = 'report-settings',
  ONLINE_REPORT_BUILDER = 'online-report-builder'
}

export const enum EventName {
  NEW_REPORT = 'new-report',
  COPY_REPORT = 'copy-report',
  EDIT_REPORT = 'edit-report',
  RUN_REPORT = 'run-report',
  DOWNLOAD_REPORT = 'download-report',
  ANALYZE_REPORT = 'analyze-report',
  CREATE_SUBSCRIPTION = 'create-subscription',
  UPDATE_SUBSCRIPTION = 'update-subscription',
  DELETE_SUBSCRIPTION = 'delete-subscription',
  EDIT_SUBSCRIPTION = 'edit-subscription',
  CANCEL_FORM = 'cancel-form',
  SEARCH_COLUMNS = 'search-columns',
  CUSTOMIZE_COLUMNS = 'customize-columns',
  RESET_TO_DEFAULT = 'reset-to-default',
  SEARCH_REPORTS = 'search-reports',
  FILTERS_UPDATED = 'filters-updated',
  HOVER_STATUS_DETAILS = 'hover-status-details',
  HOVER_MANAGER_ACCOUNTS_DETAILS = 'hover-manager-accounts-details',
  HOVER_ADVERTISER_ACCOUNTS_DETAILS = 'hover-advertiser-accounts-details',
  HOVER_ACCOUNTS_DETAILS = 'hover-accounts-details',
  UPDATE_SUBSCRIPTION_STATE = 'update-subscription-state',
  OPEN_REPORT_DETAILS = 'open-report-details',
  USE_TEMPLATE = 'use-template',
  EXPAND_TEMPLATE = 'expand-templates',
  CHANGE_PAGE = 'pagination-change',
  CHANGE_PAGE_SIZE = 'pagination-size-change',
  OPEN_ACCOUNTS_PICKER = 'open-accounts-picker',
  SAVE_ACCOUNTS_PICKER_SELECTION = 'save-accounts-picker-selection',
  SEARCH_ACCOUNTS_PICKER = 'search-accounts-picker',
  OPEN_CAMPAIGNS_PICKER = 'open-campaigns-picker',
  SAVE_CAMPAIGNS_PICKER_SELECTION = 'save-campaigns-picker-selection',
  SEARCH_CAMPAIGNS_PICKER = 'search-campaigns-picker',
  OPEN_COUNTRY_FILTER = 'open-country-filter',
  SAVE_COUNTRY_FILTER_SELECTION = 'save-country-filter-selection',
  OPEN_AD_PRODUCT_FILTER = 'open-ad-product-filter',
  SAVE_AD_PRODUCT_FILTER_SELECTION = 'save-ad-product-filter-selection'
}

export const enum EventType {
  CANCEL = 'cancel',
  CLICK = 'click',
  ERROR = 'error',
  HOVER = 'hover',
  INPUT = 'input',
  LOAD = 'load',
  SUBMIT = 'submit',
  TOGGLE = 'toggle'
}

/**
 * The delay (in milliseconds) to debounce the search input callback function.
 * This ensures that the callback is only executed after the user has stopped typing for this duration,
 * effectively capturing the final word or phrase.
 */
export const DEBOUNCE_DELAY = 3000; // 3 seconds

export type SubscriptionDetails = {
  subscriptionId?: string;
  templateId?: string;
  latestReportId?: string | null;
  managerAccountsCount: number;
  advertiserAccountsCount: number;
  campaignsCount?: number;
  dimensions: string[];
  metrics: string[];
  isMtaChecked: boolean;
  reportPeriod: string;
  recipientsCount: number;
  frequency: ScheduleType;
  reportFilters?: ReportFilter[];
  validationErrors?: string[];
};

type SearchTerm = {
  searchTerm: string;
};

type OldAndNewColumnList = {
  oldColumns: string[];
  newColumns: string[];
};

type CustomizeColumnList = {
  oldColumns: string[];
  addedColumns: string[];
  deletedColumns: string[];
};

type TemplateId = {
  templateId: string;
};

type PageSize = {
  pageSize: number;
};

export const TaktSupportedFilters: string[] = [
  SUBSCRIPTION_TABLE_COLUMN_ID.STATE,
  SUBSCRIPTION_TABLE_COLUMN_ID.STATUS,
  SUBSCRIPTION_TABLE_COLUMN_ID.FREQUENCY
];

export type Filters = {
  filters: {
    field: string;
    value: string[];
  }[];
};

type FailedReportStatus = {
  subscriptionId: string;
  reportId?: string;
  failureCode: string;
};

type SubscriptionStateUpdate = {
  subscriptionId: string;
  state: 'ACTIVE' | 'PAUSED';
};

type AccountsPayload = {
  subscriptionId: string;
  accounts: number;
};

type FilterValuePayload = {
  filterValues: string[];
  filterValueCount?: number;
};

export type TemplateModifications = {
  templateId: string;
  dimensionsModified: boolean;
  dimensionsRemoved: number;
  dimensionsAdded: number;
};

export type ReportPeriodModification = {
  dateRangeModified: boolean;
};
